import { Link, Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { Compass, House } from 'lucide-react';
import { Logo } from '@/components/shell/logo';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import type { RouterContext } from '@/router-context';

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

/**
 * Unknown URLs (T-133, A02 states): the logo over a raised card with the EmptyState look —
 * sunken icon tile, "Page not found" as the page's h1, a muted hint and one primary action back
 * to the dashboard. Rendered by the root route, so it works signed in or out (the dashboard link
 * still goes through the usual auth redirect) and follows the theme through the tokens.
 */
function NotFound() {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-background px-4 py-10 sm:px-6">
      <Link to="/" aria-label="ThriveHRM home" className="rounded-md">
        <Logo />
      </Link>
      <section
        data-slot="not-found-card"
        aria-labelledby="not-found-title"
        className="w-full max-w-120 rounded-xl border bg-surface shadow-raised"
      >
        <EmptyState
          icon={Compass}
          titleAs="h1"
          title={<span id="not-found-title">Page not found</span>}
          hint="The page you are looking for does not exist or has moved. Check the address, or start again from the dashboard."
          action={
            <Button asChild>
              <Link to="/">
                <House aria-hidden="true" />
                Go to the dashboard
              </Link>
            </Button>
          }
        />
      </section>
    </main>
  );
}
